import { ApiError } from '../services/api';

/** Spec 085 limits (requirements §4 Limits; orders `DealerCreditSettings`): whole AED, 100 … 1,000,000. */
export const MIN_AMOUNT_AED = 100;
export const MAX_FACE_VALUE_AED = 1_000_000;
export const NOTE_MAX = 500;
export const NAME_MAX = 120;

type Translator = (key: string) => string;

/** `t()` with `{name}` placeholders filled. */
export function fmt(template: string, values: Record<string, string | number>): string {
  return Object.entries(values).reduce((s, [k, v]) => s.split(`{${k}}`).join(String(v)), template);
}

function group(intPart: string): string {
  return intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/**
 * "AED 1,234.00". Credit money arrives as a 2-dp string and is only regrouped (no float maths);
 * booking money arrives as a number.
 */
export function formatAed(v: string | number | null | undefined): string {
  if (v === null || v === undefined || v === '') return '—';
  const s = typeof v === 'number' ? v.toFixed(2) : v;
  const m = /^(-?)(\d+)(?:\.(\d+))?$/.exec(s.trim());
  if (!m) return `AED ${s}`;
  const frac = (m[3] ?? '').padEnd(2, '0').slice(0, 2);
  return `AED ${m[1]}${group(m[2])}.${frac}`;
}

export function formatDate(iso: string | null | undefined, lang: string): string {
  if (!iso) return '—';
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00` : iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(lang === 'ar' ? 'ar-AE' : 'en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** E.164, e.g. +971501234567 (the backend normalises and answers 422 INVALID_PHONE otherwise). */
export function isE164(phone: string): boolean {
  return /^\+[1-9]\d{7,14}$/.test(phone.trim());
}

/** A plausible email address (the backend checks it again). */
export function isEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()) && email.trim().length <= 254;
}

/**
 * Validates a whole-AED amount against the backend limits; returns the `dealer.validation.*` key of the
 * first problem, or null.
 */
export function validateWholeAed(raw: string, max: number = MAX_FACE_VALUE_AED): string | null {
  const s = raw.trim();
  if (!s) return 'dealer.validation.amountRequired';
  if (!/^\d+$/.test(s)) return 'dealer.validation.amountWhole';
  const n = Number(s);
  if (n < MIN_AMOUNT_AED) return 'dealer.validation.amountMin';
  if (n > MAX_FACE_VALUE_AED) return 'dealer.validation.amountMax';
  if (n > max) return 'dealer.validation.amountAboveBalance';
  return null;
}

/** Backend error codes the dealer portal translates (`dealer.error.<CODE>`). */
const KNOWN_CODES = new Set([
  'FEATURE_OFF', 'NOT_FOUND', 'BUYER_NOT_FOUND', 'NOT_A_DEALER_USER', 'DEALER_SUSPENDED', 'DEALER_NOT_ACTIVE',
  'COMMIT_NOT_ALLOWED', 'OWNER_ONLY', 'INVALID_STATE', 'INVALID_AMOUNT', 'INVALID_PHONE', 'CUSTOMER_NOT_FOUND',
  'TOO_MANY_LOOKUPS',
  'TOO_MANY_OPEN_REQUESTS', 'PACK_UNAVAILABLE', 'INSUFFICIENT_DEALER_BALANCE', 'INSUFFICIENT_CREDIT',
  'CREDIT_UNAVAILABLE', 'EXCEEDS_CREDIT', 'BUYER_NO_ACCOUNT', 'NOT_ALLOWED_IN_STATE', 'PAYMENT_UNAVAILABLE',
  'VALIDATION', 'CUSTOMER_LOOKUP_UNAVAILABLE', 'BOOKING_TOTAL_CHANGED', 'ADDRESS_NOT_OWNED',
  'ZOFEUR_NOT_AVAILABLE_FOR_ADDRESS', 'ZOFEUR_BOTH_LEGS', 'PICKUP_SLOT_INVALID', 'ORDER_CHANGED',
  'CAPACITY_CONFIRM_REQUIRED',
]);

/** The backend code of an API error (`code`, else `error`). */
export function errorCodeOf(err: unknown): string | null {
  if (err instanceof ApiError) return err.errorCode ?? err.code;
  return null;
}

/** A translated message for a dealer API error; unknown codes fall back to the server's English message. */
export function dealerErrorMessage(err: unknown, t: Translator): string {
  const code = errorCodeOf(err);
  if (code === 'EXCEEDS_CREDIT' && err instanceof ApiError && err.body?.exceedsByAed != null) {
    return fmt(t('dealer.booking.exceedsBy'), { amount: formatAed(err.body.exceedsByAed as number | string) });
  }
  if (code && KNOWN_CODES.has(code)) {
    const key = `dealer.error.${code}`;
    const v = t(key);
    if (v !== key) return v;
  }
  if (err instanceof Error && err.message) return err.message;
  return t('dealer.error.generic');
}

export function isFeatureOff(err: unknown): boolean {
  return err instanceof ApiError && err.status === 404 && errorCodeOf(err) === 'FEATURE_OFF';
}
